import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Snackbar,
  Divider,
  Grid,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Checkbox
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Inbox as InboxIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PictureAsPdf as PdfIcon,
  Euro as EuroIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  QrCode2 as QrCodeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import XmlViewerDialog from '../components/XmlViewerDialog';
import { Gks02Service } from '../services/sabrinaService';
import DatevVersandPanel, { DatevHistory, DatevStatusChip, datevBlockedReason } from '../components/DatevVersandPanel';
import {
  SabrinaPosteingangResult,
  Gks02Gebuehrenbescheid,
  DatevSummary
} from '../types/sabrina';

const POLLING_COOLDOWN_SECONDS = 60;

/** Truncates a string so its UTF-8 byte representation stays within maxBytes. */
const truncateToBytes = (str: string, maxBytes: number): string => {
  const encoder = new TextEncoder();
  let result = str;
  while (encoder.encode(result).length > maxBytes) {
    result = result.substring(0, result.length - 1);
  }
  return result;
};

const buildEpcPayload = (bescheid: Gks02Gebuehrenbescheid): string => {
  const name   = truncateToBytes(bescheid.bankverbindungKontoinhaber || 'Empfänger', 70);
  const iban   = (bescheid.bankverbindungIban || '').replace(/\s/g, '');
  const bic    = bescheid.bankverbindungBic || '';
  const amount = bescheid.summeGesamt != null ? `EUR${bescheid.summeGesamt.toFixed(2)}` : '';
  const ref    = truncateToBytes(bescheid.kassenzeichen || bescheid.verwendungszweck || '', 140);
  return ['BCD', '002', '1', 'SCT', bic, name, iban, amount, '', '', ref].join('\n');
};

const formatDate = (value?: string): string => {
  if (!value) return '–';
  try {
    return new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return value;
  }
};

const formatDateTime = (value?: string): string => {
  if (!value) return '–';
  try {
    return new Date(value).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return value;
  }
};

const formatBetrag = (value?: number): string => {
  if (value === undefined || value === null) return '–';
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const formatIban = (iban?: string): string => {
  if (!iban) return '–';
  return iban.replace(/(.{4})/g, '$1 ').trim();
};

const isFaellig = (datum?: string): boolean => {
  if (!datum) return false;
  return new Date(datum) < new Date();
};

const Gks02Page: React.FC = () => {
  // ── Posteingang ──
  const [posteingangResult, setPosteingangResult] = useState<SabrinaPosteingangResult | null>(null);
  const [posteingangLoading, setPosteingangLoading] = useState(false);
  const [posteingangError, setPosteingangError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [abrufLoading, setAbrufLoading] = useState(false);

  // ── Bescheide ──
  const [bescheide, setBescheide] = useState<Gks02Gebuehrenbescheid[]>([]);
  const [bescheideLoading, setBescheideLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [includeBezahlt, setIncludeBezahlt] = useState(false);
  const [bezahlenLoading, setBezahlenLoading] = useState<number | null>(null);
  // Der Dialog merkt sich genau einen bezahlten Bescheid; die Sperre verhindert doppelte PATCH-Aufrufe.
  const [offenDialogBescheid, setOffenDialogBescheid] = useState<Gks02Gebuehrenbescheid | null>(null);
  const [offenLoading, setOffenLoading] = useState(false);
  const [qrDialogBescheid, setQrDialogBescheid] = useState<Gks02Gebuehrenbescheid | null>(null);
  const [kassenzeichenSuche, setKassenzeichenSuche] = useState('');
  const [selectedBescheidIds, setSelectedBescheidIds] = useState<number[]>([]);
  const [datevSummaries, setDatevSummaries] = useState<Record<number, DatevSummary>>({});
  const [xmlDialog, setXmlDialog] = useState<{ open: boolean; content: string; loading: boolean }>(
    { open: false, content: '', loading: false }
  );

  // ── Pagination ──
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Snackbar ──
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // ── Cooldown Timer ──
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  // ── Bescheide laden ──
  const loadBescheide = useCallback(async (withBezahlt?: boolean) => {
    setBescheideLoading(true);
    try {
      const data = await Gks02Service.getAlleBescheide(withBezahlt ?? includeBezahlt);
      setBescheide(data);
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Fehler beim Laden der Gebührenbescheide', severity: 'error' });
    } finally {
      setBescheideLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeBezahlt]);

  useEffect(() => {
    loadBescheide();
  }, [loadBescheide]);

  // ── XML laden ──
  const handleShowBescheidXml = async (id: number) => {
    setXmlDialog({ open: true, content: '', loading: true });
    try {
      const xml = await Gks02Service.getBescheidXml(id);
      setXmlDialog({ open: true, content: xml || 'Kein XML-Inhalt vorhanden', loading: false });
    } catch {
      setXmlDialog({ open: true, content: 'Fehler beim Laden des XML-Inhalts', loading: false });
    }
  };

  // ── Als bezahlt markieren ──
  const handleBezahlen = async (id: number) => {
    setBezahlenLoading(id);
    try {
      await Gks02Service.markiereAlsBezahlt(id);
      setSnackbar({ open: true, message: 'Gebührenbescheid als bezahlt markiert', severity: 'success' });
      setExpandedRowId(null);
      await loadBescheide();
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Fehler beim Markieren als bezahlt', severity: 'error' });
    } finally {
      setBezahlenLoading(null);
    }
  };

  // ── Posteingang prüfen ──
  const handleCheckPosteingang = async () => {
    setPosteingangLoading(true);
    setPosteingangError(null);
    setPosteingangResult(null);
    try {
      const result = await Gks02Service.checkPosteingang();
      setPosteingangResult(result);
      setCooldownSeconds(POLLING_COOLDOWN_SECONDS);
    } catch (error: any) {
      if (error?.response?.status === 429) {
        setPosteingangError('Zu viele Anfragen – bitte warten Sie 60 Sekunden.');
        setCooldownSeconds(POLLING_COOLDOWN_SECONDS);
      } else {
        setPosteingangError(error?.response?.data?.message || 'Fehler beim Prüfen des Posteingangs');
      }
    } finally {
      setPosteingangLoading(false);
    }
  };

  // ── Alle Nachrichten abrufen ──
  const handleAbrufAlle = async () => {
    if (!posteingangResult?.nachrichten?.length) return;
    setAbrufLoading(true);
    try {
      const ids = posteingangResult.nachrichten.map((n) => n.nachrichtenId).filter(Boolean);
      const result = await Gks02Service.abrufNachrichten(ids);
      setSnackbar({
        open: true,
        message: `Abruf erfolgreich: ${result.gespeicherteBescheide} Bescheid(e) gespeichert`,
        severity: 'success'
      });
      setPosteingangResult(null);
      setPage(0);
      await loadBescheide();
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Fehler beim Abruf der Nachrichten', severity: 'error' });
    } finally {
      setAbrufLoading(false);
    }
  };

  // ── Dokument herunterladen ──
  const handleDownload = async (dokumentId: number, dateiname?: string) => {
    try {
      await Gks02Service.downloadDokument(dokumentId);
    } catch (error: any) {
      setSnackbar({ open: true, message: 'Fehler beim Herunterladen des Dokuments', severity: 'error' });
    }
  };

  const sortedBescheide = [...bescheide].sort(
    (a, b) => new Date(b.erstelltAm).getTime() - new Date(a.erstelltAm).getTime()
  );
  const searchTerm = kassenzeichenSuche.trim().toLowerCase();
  const filteredBescheide = searchTerm
    ? sortedBescheide.filter(b => (b.kassenzeichen || '').toLowerCase().includes(searchTerm))
    : sortedBescheide;
  const paginatedBescheide = filteredBescheide.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const selectablePage = paginatedBescheide.filter(b => !datevBlockedReason(b, datevSummaries[b.id]));
  const selectedBescheide = filteredBescheide.filter(b => selectedBescheidIds.includes(b.id)
    && !datevBlockedReason(b, datevSummaries[b.id]));
  const selectionKey = filteredBescheide.filter(b => !datevBlockedReason(b, datevSummaries[b.id])).map(b => b.id).join(',');

  useEffect(() => {
    const allowed = selectionKey.split(',').map(Number);
    setSelectedBescheidIds(current => {
      const next = current.filter(id => allowed.includes(id));
      return next.length === current.length ? current : next;
    });
  }, [selectionKey]);

  useEffect(() => {
    setPage(current => Math.min(current, Math.max(0, Math.ceil(filteredBescheide.length / rowsPerPage) - 1)));
  }, [filteredBescheide.length, rowsPerPage]);

  const toggleSelection = (id: number) => setSelectedBescheidIds(current =>
    current.includes(id) ? current.filter(value => value !== id) : [...current, id]);

  /**
   * Bestaetigte Korrektur eines einzelnen Zahlungsstatus. Erst die erfolgreiche
   * PATCH-Antwort schliesst den Dialog; danach Liste und DATEV-Sammelstatus neu laden.
   * Ein 409 bei laufendem Versand wird als Backend-Meldung angezeigt, nicht lokal umgangen.
   */
  const handleAlsOffenMarkieren = async () => {
    if (!offenDialogBescheid || offenLoading) return;
    setOffenLoading(true);
    try {
      await Gks02Service.markiereAlsOffen(offenDialogBescheid.id);
      setOffenDialogBescheid(null);
      setExpandedRowId(null);
      setSnackbar({ open: true, message: 'Gebührenbescheid wieder als offen markiert', severity: 'success' });
      await loadBescheide();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Fehler beim Zurücksetzen auf offen',
        severity: 'error',
      });
    } finally {
      setOffenLoading(false);
    }
  };

  const cooldownPercent = cooldownSeconds > 0 ? (cooldownSeconds / POLLING_COOLDOWN_SECONDS) * 100 : 0;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Gebührenabrechnung
          </Typography>
          <Typography variant="body2" color="text.secondary">
            XKfz-Nachrichtentyp 0103 · Gebührenbescheide der KBA
          </Typography>
        </Box>
        <EuroIcon sx={{ fontSize: 48, color: 'primary.light', opacity: 0.6 }} />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Empfängt Gebührenbescheide vom KBA. Prüfen Sie regelmäßig den Posteingang
        und rufen Sie neue Bescheide ab. <strong>Maximal 1 Prüfung pro Minute erlaubt.</strong>
      </Alert>

      {/* ── Posteingang-Card ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Posteingang prüfen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Prüft ob neue Gebührenbescheide (0006 → 0007) im SABRINA-Posteingang vorliegen.
          </Typography>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={posteingangLoading ? <CircularProgress size={16} color="inherit" /> : <InboxIcon />}
              onClick={handleCheckPosteingang}
              disabled={posteingangLoading || cooldownSeconds > 0}
            >
              {posteingangLoading
                ? 'Wird geprüft …'
                : cooldownSeconds > 0
                ? `Warten (${cooldownSeconds}s)`
                : 'Posteingang prüfen'}
            </Button>

            {posteingangResult && posteingangResult.nachrichten?.length > 0 && (
              <Button
                variant="outlined"
                color="success"
                startIcon={abrufLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={handleAbrufAlle}
                disabled={abrufLoading}
              >
                {abrufLoading
                  ? 'Wird abgerufen …'
                  : `Alle ${posteingangResult.nachrichten.length} abrufen`}
              </Button>
            )}
          </Box>

          {cooldownSeconds > 0 && (
            <Box mt={1}>
              <LinearProgress variant="determinate" value={cooldownPercent} color="warning" />
              <Typography variant="caption" color="text.secondary">
                Nächste Prüfung möglich in {cooldownSeconds} Sekunden
              </Typography>
            </Box>
          )}

          {posteingangError && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 2 }}>
              {posteingangError}
            </Alert>
          )}

          {posteingangResult && !posteingangError && (
            <Alert
              severity={posteingangResult.nachrichten?.length > 0 ? 'success' : 'info'}
              icon={posteingangResult.nachrichten?.length > 0 ? <CheckCircleIcon /> : undefined}
              sx={{ mt: 2 }}
            >
              {posteingangResult.nachrichten?.length > 0
                ? `${posteingangResult.nachrichten.length} neue Nachricht(en) verfügbar.`
                : 'Kein neuer Eingang.'}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── Gebührenbescheide-Card ── */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">
              Gespeicherte Gebührenbescheide
              {filteredBescheide.length > 0 && (
                <Chip
                  label={searchTerm ? `${filteredBescheide.length} / ${bescheide.length}` : bescheide.length}
                  size="small"
                  sx={{ ml: 1 }}
                  color={searchTerm ? 'primary' : 'default'}
                />
              )}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeBezahlt}
                    onChange={(e) => {
                      setIncludeBezahlt(e.target.checked);
                      setPage(0);
                    }}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Bezahlte anzeigen</Typography>}
              />
              <Tooltip title="Aktualisieren">
                <IconButton onClick={() => loadBescheide()} disabled={bescheideLoading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TextField
            size="small"
            placeholder="Nach Kassenzeichen suchen …"
            value={kassenzeichenSuche}
            onChange={(e) => { setKassenzeichenSuche(e.target.value); setPage(0); }}
            sx={{ mb: 2, width: { xs: '100%', sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: kassenzeichenSuche ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setKassenzeichenSuche(''); setPage(0); }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }}
          />

          <DatevVersandPanel bescheide={bescheide} selected={selectedBescheide}
            summaries={datevSummaries} onSummaries={setDatevSummaries} onChanged={() => loadBescheide()} />

          {bescheideLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : bescheide.length === 0 ? (
            <Alert severity="info">Noch keine Gebührenbescheide gespeichert.</Alert>
          ) : filteredBescheide.length === 0 ? (
            <Alert severity="info">Kein Gebührenbescheid mit Kassenzeichen „{kassenzeichenSuche}“ gefunden.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          inputProps={{ 'aria-label': 'Versandfähige Bescheide dieser Seite auswählen' }}
                          disabled={!selectablePage.length}
                          checked={selectablePage.length > 0 && selectablePage.every(b => selectedBescheidIds.includes(b.id))}
                          indeterminate={selectablePage.some(b => selectedBescheidIds.includes(b.id))
                            && !selectablePage.every(b => selectedBescheidIds.includes(b.id))}
                          onChange={event => setSelectedBescheidIds(current => event.target.checked
                            ? Array.from(new Set([...current, ...selectablePage.map(b => b.id)]))
                            : current.filter(id => !selectablePage.some(b => b.id === id)))}
                        />
                      </TableCell>
                      <TableCell />
                      <TableCell>Bescheid-Nr.</TableCell>
                      <TableCell>Abrechnungszeitraum</TableCell>
                      <TableCell>Fälligkeit</TableCell>
                      <TableCell align="right">Betrag</TableCell>
                      <TableCell>Kassenzeichen</TableCell>
                      <TableCell>Dokumente</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>DATEV</TableCell>
                      <TableCell>Erstellt am</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedBescheide.map((bescheid) => {
                      const isExpanded = expandedRowId === bescheid.id;
                      const faellig = isFaellig(bescheid.datumFaelligkeit);
                      return (
                        <React.Fragment key={bescheid.id}>
                          <TableRow
                            hover
                            sx={{
                              '& td': { borderBottom: isExpanded ? 0 : undefined },
                              bgcolor: bescheid.bezahlt ? 'rgba(46, 125, 50, 0.06)' : undefined
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Tooltip title={datevBlockedReason(bescheid, datevSummaries[bescheid.id]) || 'Für DATEV-Versand auswählen'}>
                                <span>
                                  <Checkbox checked={selectedBescheidIds.includes(bescheid.id)}
                                    disabled={!!datevBlockedReason(bescheid, datevSummaries[bescheid.id])}
                                    inputProps={{ 'aria-label': `Bescheid ${bescheid.nummerGebuehrenbescheid || bescheid.id} auswählen` }}
                                    onClick={event => event.stopPropagation()} onChange={() => toggleSelection(bescheid.id)} />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell padding="checkbox">
                              <IconButton
                                size="small"
                                aria-label={`Details ${bescheid.nummerGebuehrenbescheid || bescheid.id} ${isExpanded ? 'schließen' : 'öffnen'}`}
                                onClick={() => setExpandedRowId(isExpanded ? null : bescheid.id)}
                              >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                              >
                                {bescheid.nummerGebuehrenbescheid || '–'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {bescheid.abrechnungszeitraumVon || bescheid.abrechnungszeitraumBis
                                ? `${formatDate(bescheid.abrechnungszeitraumVon)} – ${formatDate(bescheid.abrechnungszeitraumBis)}`
                                : '–'}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color={faellig ? 'error' : 'text.primary'}
                                fontWeight={faellig ? 600 : undefined}
                              >
                                {faellig && <WarningIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />}
                                {formatDate(bescheid.datumFaelligkeit)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={500}>
                                {formatBetrag(bescheid.summeGesamt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {bescheid.kassenzeichen || '–'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {bescheid.dokumente?.length > 0 ? (
                                <Chip
                                  icon={<PdfIcon />}
                                  label={bescheid.dokumente.length}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">–</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {bescheid.bezahlt ? (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="Bezahlt"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="Offen" size="small" color="warning" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>
                              <DatevStatusChip summary={datevSummaries[bescheid.id]} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDateTime(bescheid.erstelltAm)}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Expandierte Detailzeile */}
                          <TableRow>
                            <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 3, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Typography variant="caption" color="text.secondary">IBAN</Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {formatIban(bescheid.bankverbindungIban)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Typography variant="caption" color="text.secondary">BIC</Typography>
                                      <Typography variant="body2">
                                        {bescheid.bankverbindungBic || '–'}
                                      </Typography>
                                    </Grid>
                                    {bescheid.bankverbindungKontoinhaber && (
                                      <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="text.secondary">Kontoinhaber</Typography>
                                        <Typography variant="body2">{bescheid.bankverbindungKontoinhaber}</Typography>
                                      </Grid>
                                    )}
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Typography variant="caption" color="text.secondary">Einzugsermächtigung</Typography>
                                      <Typography variant="body2">
                                        {bescheid.bankverbindungEinzugsermaechtigung === undefined ? '–'
                                          : bescheid.bankverbindungEinzugsermaechtigung ? 'Ja' : 'Nein'}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Typography variant="caption" color="text.secondary">Anzahl abgerechneter Anträge</Typography>
                                      <Typography variant="body2">
                                        {bescheid.anzahlAbgerechneteAntraege || '–'}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Typography variant="caption" color="text.secondary">Datum Einziehung</Typography>
                                      <Typography variant="body2">
                                        {formatDate(bescheid.datumEinziehung)}
                                      </Typography>
                                    </Grid>
                                    {bescheid.verwendungszweck && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">Verwendungszweck</Typography>
                                        <Typography variant="body2">{bescheid.verwendungszweck}</Typography>
                                      </Grid>
                                    )}
                                    {bescheid.absenderKps && (
                                      <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="text.secondary">Absender KPS</Typography>
                                        <Typography variant="body2">{bescheid.absenderKps}</Typography>
                                      </Grid>
                                    )}
                                    {bescheid.sabrinaNachrichtenId && (
                                      <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">SABRINA-Nachrichten-ID</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                          {bescheid.sabrinaNachrichtenId}
                                        </Typography>
                                      </Grid>
                                    )}

                                    {bescheid.dokumente?.length > 0 && (
                                      <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                          Dokumente
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                          {bescheid.dokumente.map((dok) => (
                                            <Button
                                              key={dok.id}
                                              variant="outlined"
                                              size="small"
                                              startIcon={<PdfIcon />}
                                              onClick={() => handleDownload(dok.id, dok.dateiname)}
                                            >
                                              {dok.dateiname || dok.artDesDokuments || `Dokument ${dok.id}`}
                                            </Button>
                                          ))}
                                        </Box>
                                      </Grid>
                                    )}

                                    <Grid item xs={12}>
                                      <Typography variant="subtitle2">DATEV-Versandhistorie</Typography>
                                      <DatevHistory key={`${bescheid.id}-${datevSummaries[bescheid.id]?.letzterVersuch?.status}`}
                                        bescheidId={bescheid.id} />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Divider sx={{ my: 1 }} />
                                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        {bescheid.bezahlt ? (
                                          <>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <CheckCircleIcon color="success" fontSize="small" />
                                            <Typography variant="body2" color="success.main" fontWeight={500}>
                                              Bezahlt am {formatDateTime(bescheid.bezahltAm)}
                                            </Typography>
                                          </Box>
                                          {/* Nur bei bezahlten Bescheiden: oeffnet den Korrekturdialog, sendet noch keinen PATCH. */}
                                          <Button variant="outlined" startIcon={<RefreshIcon />}
                                            onClick={() => setOffenDialogBescheid(bescheid)}
                                            disabled={offenLoading}>
                                            Als offen markieren
                                          </Button>
                                          </>
                                        ) : (
                                          <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={
                                              bezahlenLoading === bescheid.id
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <PaymentIcon />
                                            }
                                            onClick={() => handleBezahlen(bescheid.id)}
                                            disabled={bezahlenLoading === bescheid.id}
                                          >
                                            Als bezahlt markieren
                                          </Button>
                                        )}
                                        {bescheid.bankverbindungIban && bescheid.summeGesamt != null && bescheid.summeGesamt > 0 && (
                                          <Button
                                            variant="outlined"
                                            startIcon={<QrCodeIcon />}
                                            onClick={() => setQrDialogBescheid(bescheid)}
                                          >
                                            Überweisungs-QR
                                          </Button>
                                        )}
                                        <Button
                                          variant="outlined"
                                          startIcon={<CodeIcon />}
                                          onClick={() => handleShowBescheidXml(bescheid.id)}
                                        >
                                          XML
                                        </Button>
                                      </Box>

                                    </Grid>
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredBescheide.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                labelRowsPerPage="Zeilen pro Seite:"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/*
        Einzelne Zahlungsmarkierung bewusst korrigieren. Kein Versand wird widerrufen;
        laufende DATEV-Auftraege blockiert das Backend. Solange PATCH laeuft, sind
        Bestaetigung, Abbrechen sowie Schliessen per Escape/Hintergrund gesperrt.
      */}
      <Dialog open={offenDialogBescheid !== null}
        onClose={() => !offenLoading && setOffenDialogBescheid(null)}
        aria-labelledby="offen-dialog-title" maxWidth="sm" fullWidth>
        <DialogTitle id="offen-dialog-title">Bescheid wieder als offen markieren?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Gebührenbescheid {offenDialogBescheid?.nummerGebuehrenbescheid || offenDialogBescheid?.id} wird
            wieder als offen angezeigt. Die bisherige Bezahlt-Markierung und ihr Zeitpunkt werden zurückgesetzt.
          </Typography>
          <Alert severity="info">
            Eine bereits versendete DATEV-E-Mail wird nicht zurückgerufen. Die Versandhistorie und
            die Bestätigungspflicht für erneuten Versand bleiben erhalten.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button disabled={offenLoading} onClick={() => setOffenDialogBescheid(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleAlsOffenMarkieren} disabled={offenLoading}
            startIcon={offenLoading ? <CircularProgress size={18} /> : undefined}>
            Als offen bestätigen
          </Button>
        </DialogActions>
      </Dialog>

      {/* XML-Dialog */}
      <XmlViewerDialog
        open={xmlDialog.open}
        title="Gebührenbescheid XML"
        content={xmlDialog.content}
        loading={xmlDialog.loading}
        onClose={() => setXmlDialog(prev => ({ ...prev, open: false }))}
      />

      {/* QR-Code Dialog */}
      <Dialog
        open={qrDialogBescheid !== null}
        onClose={() => setQrDialogBescheid(null)}
        maxWidth="xs"
        fullWidth
      >
        {qrDialogBescheid && (
          <>
            <DialogTitle>Überweisung per QR-Code</DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2} pt={1}>
                <QRCodeSVG
                  value={buildEpcPayload(qrDialogBescheid)}
                  size={220}
                  level="M"
                />
                <Box width="100%">
                  <Typography variant="caption" color="text.secondary">Empfänger</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {qrDialogBescheid.bankverbindungKontoinhaber || '–'}
                  </Typography>
                </Box>
                <Box width="100%">
                  <Typography variant="caption" color="text.secondary">IBAN</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formatIban(qrDialogBescheid.bankverbindungIban)}
                  </Typography>
                </Box>
                <Box width="100%" display="flex" gap={2}>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">Betrag</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatBetrag(qrDialogBescheid.summeGesamt)}
                    </Typography>
                  </Box>
                  {(qrDialogBescheid.kassenzeichen || qrDialogBescheid.verwendungszweck) && (
                    <Box flex={2}>
                      <Typography variant="caption" color="text.secondary">Verwendungszweck</Typography>
                      <Typography variant="body2">
                        {qrDialogBescheid.kassenzeichen || qrDialogBescheid.verwendungszweck}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  EPC-QR-Code (GiroCode) – von allen deutschen Banking-Apps lesbar
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQrDialogBescheid(null)}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Gks02Page;
